/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import Modal from '../Modal'
import AckViewDocDialog from '../AckViewDocDialog'
import { useModal } from '../../contexts/ModalContext'

const btnMinWidth = '100px'
const CustomModalBody = styled.div`
  padding: 10px 15px 30px !important;
`

export interface AckCompletedDialogTask {
  pdfInfo?: {
    fsiId: number
    versionId?: number
    minorVersionId?: number
  }
  pdfSrc?:
    | string
    | { service: () => Promise<{ url: string | null; uuidToCheckState: string | null | undefined; message?: string }> }
  thumbnailId?: number | string
  downloadFn?: () => void | Promise<void>
}

interface AckCompletedDialogProps {
  task: AckCompletedDialogTask
  onClose?: () => void
}

/**
 * AckCompletedDialog - Dialog shown after Power User completes acknowledgement task
 * Provides options to view the document or download it
 * Migrated from lego/page/home/eos/AckCompletedDialog.jsx
 */
const AckCompletedDialog: React.FC<AckCompletedDialogProps> = ({ task, onClose }) => {
  const { t: translate } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const { showModal } = useModal()

  useEffect(() => {
    setIsOpen(true)
  }, [])

  const doClose = (isTerminateFlow: boolean = true) => {
    setIsOpen(false)
    if (onClose && isTerminateFlow) {
      onClose()
    }
  }

  const handleViewDocument = () => {
    doClose(false)
    showModal(
      <AckViewDocDialog
        pdfSrc={task.pdfSrc as any}
        pdfInfo={task.pdfInfo}
        thumbnailId={task.thumbnailId}
        downloadFn={task.downloadFn}
        onClose={onClose}
      />
    )
  }

  const handleDownload = () => {
    if (task.downloadFn) {
      task.downloadFn()
    }
  }

  return (
    <Modal
      show={isOpen}
      handleClose={() => doClose(true)}
      title={translate('EOS_DASHBOARD_TASK_ACKNOWLEDGEMENT_COMPLETED_TITLE')}
      className="eos-complete-ack-task-modal"
    >
      <Modal.Body>
        <CustomModalBody>
          <div>
            <b>{translate('EOS_DASHBOARD_TASK_ACKNOWLEDGEMENT_COMPLETED_MSG')}</b>
          </div>
          <div className="d-flex mt25">
            <div className="d-flex">
              <Button
                style={{ minWidth: btnMinWidth }}
                className="btn btn-primary text-capitalize mr10"
                onClick={handleViewDocument}
                id="8b1c39d1-b726-4ef3-9a6f-fe91fd0cbac3"
              >
                {translate('EOS_DASHBOARD_TASK_COMPLETED_VIEW_BTN')}
              </Button>
              <Button
                style={{ minWidth: btnMinWidth }}
                className="btn btn-primary text-capitalize mr10"
                onClick={handleDownload}
                id="21523acb-399b-425b-99ca-803655501ecc"
              >
                {translate('EOS_DASHBOARD_TASK_COMPLETED_DOWNLOAD_BTN')}
              </Button>
            </div>
          </div>
        </CustomModalBody>
      </Modal.Body>
    </Modal>
  )
}

export default AckCompletedDialog
